import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Coins, Sparkles } from 'lucide-react';
import FileDropzone from '../components/features/FileDropzone';
import GradeReportView from '../components/features/GradeReportView';
import { parseFile, ParsedFile } from '../services/fileParser';
import { gradeReport } from '../services/gradingService';
import { saveJob, uploadReportFile } from '../services/jobsService';
import { useAuth } from '../contexts/AuthContext';
import type { GradeReport } from '../types';

export default function Evaluate() {
  const { user, addCredits } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GradeReport | null>(null);

  const handleFile = async (f: File) => {
    setFile(f);
    setParsed(null);
    setResult(null);
    try {
      const p = await parseFile(f);
      setParsed(p);
      toast.success(`Parsed ${p.wordCount.toLocaleString()} words`);
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to parse file');
      setFile(null);
    }
  };

  const clear = () => { setFile(null); setParsed(null); setResult(null); };

  const runGrade = async () => {
    if (!parsed) return;
    if (!user || user.credits < 1) {
      toast.error('You need at least 1 credit to evaluate a report.');
      return;
    }
    setLoading(true);
    try {
      const r = await gradeReport(parsed);
      setResult(r);
      addCredits(-1);
      // Persist: upload source to Storage, write metadata job to Firestore.
      // Both are fire-and-forget — the result is already rendered for the user.
      const storagePath = file ? await uploadReportFile(file, r.id) : undefined;
      await saveJob({
        kind: 'evaluation',
        fileName: r.fileName,
        storagePath,
        score: r.overallScore,
        grade: r.letterGrade,
      });
      toast.success('Evaluation complete');
    } catch (e: any) {
      toast.error(e.message ?? 'Grading failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadJson = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${result.fileName.replace(/\.[^.]+$/, '')}.evaluation.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-wide py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold">Evaluate a report</h1>
          <p className="text-ink-600 mt-1">Upload a .docx or .pdf file. Your first evaluation is free.</p>
        </div>
        {user && (
          <span className="chip bg-brand-50 text-brand-700 border border-brand-100 px-3 py-1.5 text-sm">
            <Coins className="w-4 h-4 mr-1.5" /> {user.credits} credits
          </span>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <FileDropzone onFile={handleFile} selectedFile={file} onClear={clear} />

          {parsed && (
            <div className="card p-5">
              <h3 className="font-semibold">Document overview</h3>
              <dl className="mt-3 text-sm grid grid-cols-2 gap-x-4 gap-y-2">
                <dt className="text-ink-500">Words</dt>       <dd className="text-right">{parsed.wordCount.toLocaleString()}</dd>
                <dt className="text-ink-500">Sections found</dt><dd className="text-right">{parsed.sections.length}</dd>
                <dt className="text-ink-500">File</dt>        <dd className="text-right truncate">{parsed.fileName}</dd>
              </dl>
              <div className="mt-4">
                <div className="text-xs text-ink-500 mb-2">Detected sections</div>
                <div className="flex flex-wrap gap-1.5">
                  {parsed.sections.slice(0, 8).map((s, i) => (
                    <span key={i} className="chip bg-ink-100 text-ink-700 text-xs">{s.name}</span>
                  ))}
                </div>
              </div>
              <button onClick={runGrade} disabled={loading} className="btn-primary w-full mt-5">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Grading…</> : <><Sparkles className="w-4 h-4" /> Evaluate now (1 credit)</>}
              </button>
              {user && user.credits < 1 && (
                <p className="mt-2 text-xs text-amber-700">
                  Out of credits — <Link to="/pricing" className="underline">top up</Link>.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {!result && !loading && (
            <div className="card p-10 text-center text-ink-500">
              <p className="text-sm">Upload a report to see scores here.</p>
            </div>
          )}
          {loading && (
            <div className="card p-10 text-center">
              <Loader2 className="w-8 h-8 text-brand-600 animate-spin mx-auto" />
              <p className="mt-3 text-sm text-ink-600">Analyzing structure, clarity, evidence, and mechanics…</p>
            </div>
          )}
          {result && <GradeReportView report={result} onDownload={downloadJson} />}
        </div>
      </div>
    </div>
  );
}

